import { logError } from '@ptek/client-logger';
import { useCallback, useState } from 'react';
import { documentsClient } from '../../../api-clients/documents';

export interface SegmentOverride {
  text: string;
  editedAt: string;
  editedBy: string;
}

interface UseTranscriptSaveProps {
  transcriptDocumentId: string | undefined;
}

interface TranscriptSaveContent {
  editOverrides?: Record<string, SegmentOverride>;
}

interface UseTranscriptSaveReturn {
  saveTranscript: (content: TranscriptSaveContent) => Promise<boolean>;
  isSaving: boolean;
  saveError: string | null;
  clearSaveError: () => void;
}

export function useTranscriptSave({
  transcriptDocumentId,
}: UseTranscriptSaveProps): UseTranscriptSaveReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const clearSaveError = useCallback(() => setSaveError(null), []);

  const saveTranscript = useCallback(
    async (content: TranscriptSaveContent): Promise<boolean> => {
      if (!transcriptDocumentId) return false;

      setSaveError(null);
      setIsSaving(true);
      try {
        await documentsClient.updateDocument(transcriptDocumentId, {
          generatedContent: content as Record<string, unknown>,
        });
        return true;
      } catch (error) {
        logError(
          'useTranscriptSave',
          'Failed to save transcript:',
          error
        );
        setSaveError('Failed to save edit. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [transcriptDocumentId]
  );

  return {
    saveTranscript,
    isSaving,
    saveError,
    clearSaveError,
  };
}
